import { camelizeKeys } from 'humps';
import _ from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { produce } from 'immer';

import { useActionCable } from '@/hooks/useActionCable';
import { useChannel } from '@/hooks/useChannel';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import { useGetSecureMessageThreadsQuery } from '@/patient-app-common/api/secureMessageApi';
import {
  ActionCablePayload,
  ThreadPayload,
} from '@/patient-app-common/types/SecureMessageThreads';
import { useAppSelector } from '@/redux/hooks';

type ActionCableContextType = {
  secureMessageThreads: ThreadPayload[];
  error: unknown;
  isLoading: boolean;
  inboxCount: number;
  markThreadAsRead: (id: number) => void;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ActionCableContext = createContext<ActionCableContextType>({
  secureMessageThreads: [],
  error: null,
  isLoading: false,
  inboxCount: 0,
  markThreadAsRead: () => null,
});

export const ActionCableProvider = ({ children }: { children: ReactNode }) => {
  const { patientId } = useAppSelector(({ auth }) => auth);
  const { data: patient } = useGetPatientQuery(undefined, {
    skip: !patientId,
  });

  if (!patientId || !patient?.secureMessagingEnabled) {
    return <>{children}</>;
  }

  return <ThreadsProvider>{children}</ThreadsProvider>;
};

const ThreadsProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useAppSelector(({ auth }) => auth);
  const { route } = useAppSelector(({ app }) => app);

  const [secureMessageThreads, setSecureMessageThreads] = useState<
    ThreadPayload[]
  >([]);

  const {
    data: threads,
    error,
    isLoading,
  } = useGetSecureMessageThreadsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    // refresh cache every hour
    pollingInterval: 3600000,
  });

  // In dev mode, use local proxy to avoid server blocking WebSocket from localhost
  const wssRoute = import.meta.env.DEV
    ? `ws://${window.location.host}`
    : route
      ? _.replace(route, 'https://', 'wss://')
      : 'ws://0.0.0.0:3000';

  const { actionCable } = useActionCable(`${wssRoute}/cable?token=${token}`);
  const { subscribe, unsubscribe } = useChannel(actionCable);

  const inboxCount = useMemo(() => {
    if (secureMessageThreads) {
      return _.filter(secureMessageThreads, ['unreadMessage', true]).length;
    }
    return 0;
  }, [secureMessageThreads]);

  const handleReceived = useCallback((data: unknown) => {
    const payload = camelizeKeys(data) as ActionCablePayload;

    const newMessage = {
      createdAt: payload.createdAt,
      file: payload.file,
      fileType: payload.fileType,
      id: payload.secureMessageId,
      sentBy: payload.sentBy,
      sentByName: payload.sentByName,
      text: payload.text,
    };

    const {
      closeThreadOnPatientView,
      patientReplyEnabled,
      resolved,
      unreadMessage,
    } = payload;

    setSecureMessageThreads(
      produce((draft) => {
        const existingThread = draft.find(
          (thread) => thread.id === payload.secureMessageThreadId
        );

        if (existingThread) {
          existingThread.messages.push(newMessage);
          existingThread.lastMessageSentAt = payload.createdAt;
          existingThread.patientReplyEnabled = patientReplyEnabled;
          existingThread.resolved = resolved;
          existingThread.unreadMessage = unreadMessage;
          existingThread.closeThreadOnPatientView = closeThreadOnPatientView;
        } else {
          const newThread = {
            createdAt: payload.createdAt,
            id: payload.secureMessageThreadId,
            lastMessageSentAt: payload.createdAt,
            messages: [newMessage],
            patientReplyEnabled,
            resolved,
            unreadMessage,
            closeThreadOnPatientView,
          };

          draft.push(newThread);
        }
      })
    );
  }, []);

  const markThreadAsRead = useCallback((id: number) => {
    setSecureMessageThreads(
      produce((draft) => {
        const existingThread = draft.find((thread) => thread.id === id);

        if (existingThread) {
          existingThread.unreadMessage = false;
          existingThread.resolved = existingThread.closeThreadOnPatientView;
        }
      })
    );
  }, []);

  useEffect(() => {
    subscribe(
      {
        channel: 'PatientChannel',
      },
      {
        received: (data: unknown) => {
          handleReceived(data);
        },
      }
    );
    return () => {
      unsubscribe();
    };
  }, [handleReceived]);

  useEffect(() => {
    if (!threads) return;
    setSecureMessageThreads(threads);
  }, [threads]);

  const contextData = useMemo(
    () => ({
      secureMessageThreads,
      error,
      isLoading,
      inboxCount,
      markThreadAsRead,
    }),
    [secureMessageThreads, error, isLoading, inboxCount, markThreadAsRead]
  );

  return (
    <ActionCableContext.Provider value={contextData}>
      {children}
    </ActionCableContext.Provider>
  );
};
